import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Image from 'next/image';
import { useTranslation, Trans } from 'next-i18next';

import { selectMobilePlanTitle } from '@selectors/mobilePlan';

import { getErrorMessage } from '@utils/validation';

import {
  ABONEMENT_CONTRACT_MODEL,
  PREPAID_CONTRACT_MODEL,
} from '@common/constants/mobileContractModels';
import {
  PhoneNumberInput,
  FormControl,
  RadioButton,
  InputsWrapper,
  AdditionalInfo,
  AdditionalInfoWrapper,
  SectionHeading,
  SubSectionHeading,
} from '@common/components/forms';
import Hr from '@common/components/Hr';
import { BUTTON_VARIANTS } from '@common/components/Button';
import { useMultiStepContext } from '@common/contexts/multiStepContext';
import { COUNTRY_PHONE_PREFIX_SWITZERLAND } from '@common/constants/countries';
import { SIZES } from '@common/components/forms/RadioButton/constants';
import {
  StepTitle,
  ProvidersAdditional,
  ButtonText,
  NextButton,
  ImageIconWrapper,
  RadiosWrapper,
} from '../MobileOrderForm.styled';
import { CostsInfo } from '../components';
import { FormContext } from '../FormWrapper';
import { validateAboDetailsStep } from '../MobileOrderForm.helpers';

import { PhoneLink } from './PhoneLink';

const SECTION_TRANSLATION_PREFIX = 'mobile.orderForm.aboDetails';

const AboDetails = () => {
  const { formik, handleChange, providersList } = useContext(FormContext);
  const mobilePlanTitle = useSelector(selectMobilePlanTitle);
  const { t } = useTranslation();
  const { next, handleChangeStepsValidity } = useMultiStepContext();

  const { values, touched, errors, isSubmitting, handleBlur } = formik;

  const isAboDetailsStepValid = validateAboDetailsStep(formik);

  useEffect(() => {
    handleChangeStepsValidity(0, isAboDetailsStepValid);
  }, [handleChangeStepsValidity, isAboDetailsStepValid]);

  return (
    <React.Fragment>
      <StepTitle>
        {t('mobile.orderForm.meta.description', '{{planName}} Abo bestellen', {
          planName: mobilePlanTitle,
        })}
      </StepTitle>
      <CostsInfo />
      <SectionHeading>
        {t(
          `${SECTION_TRANSLATION_PREFIX}.phoneNumberSectionHeading`,
          'Telefonnummer',
        )}
      </SectionHeading>
      <RadiosWrapper>
        <RadioButton
          id="newPhoneNum"
          name="is_existing_number"
          checked={values.is_existing_number === 'no'}
          onChange={handleChange}
          value="no"
          text={t(
            `${SECTION_TRANSLATION_PREFIX}.phoneNumberNew`,
            'Neue Telefonnummer',
          )}
          size={SIZES.LARGE}
        />
        <RadioButton
          id="existingPhoneNum"
          name="is_existing_number"
          onChange={handleChange}
          checked={values.is_existing_number === 'yes'}
          value="yes"
          text={t(
            `${SECTION_TRANSLATION_PREFIX}.phoneNumberExisting`,
            'Telefonnummer behalten',
          )}
          size={SIZES.LARGE}
        />
      </RadiosWrapper>
      {values.is_existing_number === 'no' && (
        <AdditionalInfo spacingTop={['scale', 3]}>
          {t(
            `${SECTION_TRANSLATION_PREFIX}.newPhoneNumberAdditionalInfo`,
            'Deine neue Nummer erhälst Du von Salt.',
          )}
        </AdditionalInfo>
      )}
      {values.is_existing_number === 'yes' && (
        <React.Fragment>
          <SubSectionHeading
            spacingTop={['scale', 1]}
            spacingBottom={['scale', 4]}
          >
            {t(
              `${SECTION_TRANSLATION_PREFIX}.currentPhoneNumber`,
              'Aktuelle Telefonnummer',
            )}
          </SubSectionHeading>
          <InputsWrapper>
            <FormControl
              hasError={
                touched.existing_phone_number_input &&
                !!errors.existing_phone_number
              }
              message={
                touched.existing_phone_number_input &&
                getErrorMessage(t, errors.existing_phone_number)
              }
              label={t(
                `${SECTION_TRANSLATION_PREFIX}.mobilePhoneLabel`,
                'Mobile',
              )}
              noSpacing
            >
              <PhoneNumberInput
                name="existing_phone_number"
                value={values.existing_phone_number}
                onBlur={handleBlur}
                setFieldValue={(name, value) =>
                  handleChange({
                    target: { name, value },
                  })
                }
                prefixFieldName="existing_phone_prefix"
                numberFieldName="existing_phone_number_input"
                prefixFieldId="phonePrefix"
                numberFieldId="phoneNumber"
                hasError={
                  touched.existing_phone_number_input &&
                  !!errors.existing_phone_number
                }
                disabled={isSubmitting}
                fixedPrefix={COUNTRY_PHONE_PREFIX_SWITZERLAND}
                withSpacing
              />
            </FormControl>
          </InputsWrapper>
          <AdditionalInfo>
            {t(
              `${SECTION_TRANSLATION_PREFIX}.existingPhoneNumberAdditionalInfo`,
              'Diese Nummer darf nicht auf den Namen eines Unternehmens lauten und Du musst Inhaber dieser Nummer sein. Anderenfalls bitten wir Dich, bei Deinem aktuellen Anbieter einen Inhaberwechsel zu beantragen, bevor Du Dein neues Abo bestellen kannst.s',
            )}
          </AdditionalInfo>
          <Hr spacings={['scale', 0]} />
          <SectionHeading>
            {t(
              `${SECTION_TRANSLATION_PREFIX}.contractModelSectionHeading`,
              'Aktuelle Vertragsart',
            )}
          </SectionHeading>
          <RadiosWrapper>
            <RadioButton
              id="abonament"
              name="current_contract_model"
              onChange={handleChange}
              value={ABONEMENT_CONTRACT_MODEL}
              checked={
                values.current_contract_model === ABONEMENT_CONTRACT_MODEL
              }
              size={SIZES.LARGE}
              text={t(
                `${SECTION_TRANSLATION_PREFIX}.contractModelSubscribtion`,
                'Abonnement',
              )}
            />
            <RadioButton
              id="prepaid"
              name="current_contract_model"
              onChange={handleChange}
              value={PREPAID_CONTRACT_MODEL}
              checked={values.current_contract_model === PREPAID_CONTRACT_MODEL}
              size={SIZES.LARGE}
              text={t(
                `${SECTION_TRANSLATION_PREFIX}.contractModelPrepaid`,
                'Prepaid',
              )}
            />
          </RadiosWrapper>
          {values.current_contract_model && (
            <React.Fragment>
              <SectionHeading spacingTop={['section', 3]}>
                {t(
                  `${SECTION_TRANSLATION_PREFIX}.currentProviderSectionTitle`,
                  'Aktuelle Vertragsart',
                )}
              </SectionHeading>
              <InputsWrapper hasFiveInLine>
                {providersList.map(({ id, name, logo }) => (
                  <RadioButton
                    id={id.toString()}
                    key={id}
                    name="current_provider"
                    onChange={handleChange}
                    value={id.toString()}
                    checked={id.toString() === values.current_provider}
                    isImage
                    size={SIZES.LARGE}
                    text={
                      <ImageIconWrapper>
                        <Image src={logo} alt={name} layout="fill" />
                      </ImageIconWrapper>
                    }
                  />
                ))}
              </InputsWrapper>
              <ProvidersAdditional>
                <Trans
                  i18nKey={`${SECTION_TRANSLATION_PREFIX}.saltBenefitInfo`}
                  t={t}
                  components={[
                    <PhoneLink
                      key={`${SECTION_TRANSLATION_PREFIX}.saltBenefitInfo`}
                    />,
                  ]}
                />
              </ProvidersAdditional>
              <AdditionalInfoWrapper spacingTop={['section', 3]}>
                <AdditionalInfo
                  spacingTop={['equal', 2]}
                  spacingBottom={['equal', 2]}
                >
                  {t(
                    `${SECTION_TRANSLATION_PREFIX}.numberTransferInfoPart1`,
                    'Der Transfer Deiner Nummer wird durchgeführt, sobald Dein Vertrag bei Deinem aktuellen Anbieter abgelaufen ist.',
                  )}
                </AdditionalInfo>
                <AdditionalInfo spacingTop={['equal', 2]}>
                  {t(
                    `${SECTION_TRANSLATION_PREFIX}.numberTransferInfoPart2`,
                    'Dieser Vorgang ist automatisch. Bei Deinem aktuellen Anbieter entstehen keine Gebühren für Vertragskündigung.',
                  )}
                </AdditionalInfo>
              </AdditionalInfoWrapper>
            </React.Fragment>
          )}
        </React.Fragment>
      )}
      <NextButton
        onClick={next}
        type="button"
        variant={BUTTON_VARIANTS.LEVEL_1_GREEN}
        disabled={!isAboDetailsStepValid}
      >
        <ButtonText>
          {t(`${SECTION_TRANSLATION_PREFIX}.buttonNext`, 'Nächster Schritt.')}
        </ButtonText>
      </NextButton>
    </React.Fragment>
  );
};

export default AboDetails;
